import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  WindIcon, ActivityIcon, ZapIcon,
  Link2Icon, CheckIcon, XIcon, GaugeIcon, RefreshCwIcon, CloudIcon,
  ShieldCheckIcon, FanIcon, AlertCircleIcon
} from 'lucide-react';
import {
  ComposedChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Line
} from 'recharts';

import { useTelemetry } from '../hooks/useTelemetry';
import { useTelemetryHistory } from '../hooks/useHistoricalTelemetry';
import { tbToErvCo2ChartData } from '../utils/tbChartHelper';
import { thingsboardService } from '../services/thingsboard';
import { useTranslation } from 'react-i18next';
import { formatErvStatus } from '../utils/sensorUtils';

import {
  TimeRange, SensorDetailSidebarProps, TimeRangeTabs,
  SensorHeader
} from './SensorDetailShared';

// ─── SES Connect / Link Popup (Control Relation: SES -> SEV) ───
function SevSesConnectPopup({
  sensor,
  currentSesId,
  onClose,
  onLinked
}: {
  sensor: any;
  currentSesId: string;
  onClose: () => void;
  onLinked: (ses: { id: string; name: string } | null) => void;
}) {
  const [sesDevices, setSesDevices] = useState<{ id: string; name: string }[]>([]);
  const [selectedSesId, setSelectedSesId] = useState<string>(currentSesId);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [zoneName, setZoneName] = useState('');

  useEffect(() => {
    async function loadSes() {
      try {
        setLoading(true);
        // Find existing linked SES: FROM SEV -> TO SES
        const fromRels = await thingsboardService.findRelationsByFrom('DEVICE', sensor.id).catch(() => []);
        const relOut = fromRels.find((r: any) => 
          (r.type === 'Control' || r.type?.toLowerCase() === 'control') && r.to?.entityType === 'DEVICE'
        );
        if (relOut) {
          setSelectedSesId(relOut.to.id);
        } else {
          const toRels = await thingsboardService.findRelationsByTo('DEVICE', sensor.id).catch(() => []);
          const relIn = toRels.find((r: any) => 
            (r.type === 'Control' || r.type?.toLowerCase() === 'control') && r.from?.entityType === 'DEVICE'
          );
          if (relIn) {
            setSelectedSesId(relIn.from.id);
          } else if (currentSesId) {
            setSelectedSesId(currentSesId);
          }
        }

        const data = await thingsboardService.getZoneSesDevices(sensor.id);
        setSesDevices(data.sesDevices);
        setZoneName(data.zoneName);
      } catch (err) {
        console.error('Failed to load SES devices for SEV:', err);
      } finally {
        setLoading(false);
      }
    }
    loadSes();
  }, [sensor.id]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      // 1. Delete previous Control relation (FROM: SEV -> TO: SES and reverse)
      if (currentSesId && currentSesId !== selectedSesId) {
        await Promise.all([
          thingsboardService.deleteRelation(sensor.id, 'DEVICE', currentSesId, 'DEVICE', 'Control', 'COMMON').catch(() => {}),
          thingsboardService.deleteRelation(currentSesId, 'DEVICE', sensor.id, 'DEVICE', 'Control', 'COMMON').catch(() => {})
        ]);
      }

      // 2. Create new Control relation: FROM SEV (sensor.id) -> TO SES (selectedSesId)
      if (selectedSesId) {
        await thingsboardService.createRelation(
          sensor.id,
          'DEVICE',
          selectedSesId,
          'DEVICE',
          'Control',
          'COMMON'
        ).catch(() => {});
        const found = sesDevices.find(s => s.id === selectedSesId);
        onLinked(found || { id: selectedSesId, name: 'SES Switch' });
      } else {
        onLinked(null);
      }
      onClose();
    } catch (err) {
      console.error('Failed to update SEV-SES Control relation:', err);
      alert('연결 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        className="bg-[#0f172a] border border-white/15 rounded-2xl p-5 max-w-sm w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between pb-3 mb-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400">
              <Link2Icon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">SES 스위치 연동 (Control)</h3>
              <p className="text-xs text-slate-400">{sensor.label} ({zoneName || '구역'})</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="py-8 flex flex-col items-center justify-center gap-2 text-slate-400">
            <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs">스위치 목록 조회 중...</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-400 font-medium">
              CO₂ 농도에 따라 ERV 풍량을 자동 제어하는 <span className="text-cyan-300 font-bold">{zoneName || '해당 구역'}</span>의 SES 센서를 선택하세요.
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {/* Option: Unlink / None */}
              <label
                className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                  selectedSesId === ''
                    ? 'bg-rose-500/15 border-rose-500/40 text-white'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <input
                    type="radio"
                    name="sev-ses-select"
                    checked={selectedSesId === ''}
                    onChange={() => setSelectedSesId('')}
                    className="accent-rose-400"
                  />
                  <span className="text-xs font-semibold">연동 안 함 (해제)</span>
                </div>
                {currentSesId === '' && (
                  <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full">현재 상태</span>
                )}
              </label>

              {/* List SES devices */}
              {sesDevices.length === 0 ? (
                <div className="py-4 text-center text-xs text-slate-500 bg-white/5 rounded-xl border border-white/5">
                  해당 공간에 등록된 SES 스위치가 없습니다.
                </div>
              ) : (
                sesDevices.map((ses) => {
                  const isSelected = selectedSesId === ses.id;
                  const isCurrent = currentSesId === ses.id;
                  return (
                    <label
                      key={ses.id}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-cyan-500/20 border-cyan-400/50 text-white shadow-lg shadow-cyan-950/40'
                          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <input
                          type="radio"
                          name="sev-ses-select"
                          checked={isSelected}
                          onChange={() => setSelectedSesId(ses.id)}
                          className="accent-cyan-400"
                        />
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-white">{ses.name}</span>
                          <span className="text-[10px] text-slate-400">CO₂ 멀티 스위치</span>
                        </div>
                      </div>
                      {isCurrent && (
                        <span className="text-[10px] bg-cyan-500/30 text-cyan-300 border border-cyan-400/40 px-2 py-0.5 rounded-full font-bold">
                          연동됨
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>

            <div className="flex gap-2 justify-end pt-3 mt-3 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-xs font-semibold text-slate-300 hover:bg-white/10 transition-all"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold shadow-lg shadow-cyan-500/25 transition-all disabled:opacity-50"
              >
                <CheckIcon className="w-3.5 h-3.5" />
                {isSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Custom Step-After Tooltip ───
// ─── Custom Step-After Tooltip ───
function ErvCo2CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    const ervItem = payload.find((p: any) => p.dataKey === 'erv');
    const co2Item = payload.find((p: any) => p.dataKey === 'co2');
    const ervVal = ervItem ? Number(ervItem.value) : 0;
    const co2Val = co2Item && co2Item.value !== null && !isNaN(Number(co2Item.value)) ? Number(co2Item.value) : null;
    const status = formatErvStatus(ervVal);

    const ts = typeof label === 'number' ? label : payload[0]?.payload?.ts;
    const d = ts ? new Date(ts) : new Date();
    const timeStr = d.toLocaleString('ko-KR', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    return (
      <div className="p-3 bg-[#0d1527]/95 border border-white/15 rounded-xl shadow-2xl backdrop-blur-md text-xs font-sans space-y-1.5 min-w-[150px]">
        <div className="text-slate-400 font-mono text-[11px] border-b border-white/10 pb-1">
          {timeStr}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-slate-300 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            ERV 풍량
          </span>
          <span className={`font-bold font-mono ${status?.color || 'text-cyan-300'}`}>
            {ervVal}단계 ({status?.text || '정지'})
          </span>
        </div>
        {co2Val !== null && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-slate-300 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              SES CO₂
            </span>
            <span className="font-bold font-mono text-amber-300">
              {co2Val} ppm
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
}

// ─── Fallback Data (Empty if no telemetry) ───
const FALLBACK_ERV_CO2_DATA: any[] = [];

export function SEVDetail({ sensor, onBack, onClose, onUpdateSensor }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;

  // 1. Realtime Telemetry for SEV
  const { data: realtimeData } = useTelemetry(deviceId, [
    'erv'
  ]);

  // 2. State & TimeRange (default to 'daily' to display full 24h history)
  const [ervRange, setErvRange] = useState<TimeRange>('daily');
  const [powerRange, setPowerRange] = useState<TimeRange>('daily');

  const [linkedSes, setLinkedSes] = useState<{ id: string; name: string } | null>(null);
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [localErvLevel, setLocalErvLevel] = useState<number | null>(null);

  // 3. Fetch Linked SES from Control relation: (Check both directions: SES -> SEV or SEV -> SES)
  useEffect(() => {
    async function fetchLinkedSes() {
      try {
        // Query outgoing relation: FROM SEV (deviceId) -> TO SES
        const fromRels = await thingsboardService.findRelationsByFrom('DEVICE', deviceId).catch(() => []);
        const relOut = fromRels.find((r: any) => 
          (r.type === 'Control' || r.type?.toLowerCase() === 'control') && r.to?.entityType === 'DEVICE'
        );

        if (relOut) {
          setLinkedSes({ id: relOut.to.id, name: relOut.toName || 'SES Switch' });
          return;
        }

        // Fallback check incoming: FROM SES -> TO SEV
        const toRels = await thingsboardService.findRelationsByTo('DEVICE', deviceId).catch(() => []);
        const relIn = toRels.find((r: any) => 
          (r.type === 'Control' || r.type?.toLowerCase() === 'control') && r.from?.entityType === 'DEVICE'
        );

        if (relIn) {
          setLinkedSes({ id: relIn.from.id, name: relIn.fromName || 'SES Switch' });
          return;
        }

        setLinkedSes(null);
      } catch (err) {
        console.error('Failed to fetch linked SES for SEV:', err);
      }
    }
    fetchLinkedSes();
  }, [deviceId]);

  // 4. Realtime Telemetry for Linked SES (CO2)
  const { data: sesRealtimeData } = useTelemetry(linkedSes?.id || '', ['CO2']);

  // 5. Historical Telemetry for SEV & SES
  const { data: histErv } = useTelemetryHistory(deviceId, ['erv'], ervRange, 'DEVICE', 'NONE');
  const { data: histSesCo2 } = useTelemetryHistory(linkedSes?.id || '', ['CO2'], ervRange, 'DEVICE', 'AVG');
 
  // 6. Current Live ERV level calculation (0: 정지, 1: 약풍, 2: 중풍, 3: 강풍)
  const rawLiveErv = realtimeData?.['erv']?.[0]?.value;
  const currentErvNum = localErvLevel !== null 
    ? localErvLevel 
    : rawLiveErv !== undefined 
    ? (Number(rawLiveErv) >= 0 ? Number(rawLiveErv) : 0) 
    : 0;
  const currentStatus = formatErvStatus(currentErvNum);

  // 7. Handle Manual ERV Control (RPC)
  const handleSetErv = async (level: number) => {
    setLocalErvLevel(level); // Optimistic UI update
    try {
      await thingsboardService.sendOneWayRpc(deviceId, 'set_erv', { value: level });
    } catch (err) {
      console.error('Failed to send RPC set_erv to SEV:', err);
    }
  };

  // 8. Prepare Combined Chart Data for ERV Step & CO2 Overlay
  const finalErvCo2Data = useMemo(() => {
    const ervSource = ervRange === 'realtime' ? realtimeData : histErv;
    const co2Source = ervRange === 'realtime' ? sesRealtimeData : histSesCo2;
    return tbToErvCo2ChartData(ervSource, co2Source, ervRange, FALLBACK_ERV_CO2_DATA);
  }, [ervRange, realtimeData, histErv, sesRealtimeData, histSesCo2]);

  // 9. Time Domain & Uniform Ticks Calculation (Prevents left-side clustering)
  const { chartTimeBounds, xTicks } = useMemo(() => {
    const now = Date.now();
    let windowMs = 24 * 60 * 60 * 1000;
    let tickCount = 6;
    if (ervRange === 'realtime') {
      windowMs = 30 * 60 * 1000;
      tickCount = 6;
    } else if (ervRange === 'weekly') {
      windowMs = 7 * 24 * 60 * 60 * 1000;
      tickCount = 7;
    } else if (ervRange === 'monthly') {
      windowMs = 30 * 24 * 60 * 60 * 1000;
      tickCount = 6;
    }
    const startTs = now - windowMs;
    const endTs = now;
    const step = windowMs / tickCount;
    const ticks: number[] = [];
    for (let i = 0; i <= tickCount; i++) {
      ticks.push(startTs + i * step);
    }
    return {
      chartTimeBounds: { startTs, endTs },
      xTicks: ticks
    };
  }, [ervRange]);

  const formatXAxisTick = useCallback((ts: number) => {
    const d = new Date(ts);
    if (ervRange === 'realtime' || ervRange === 'daily') {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    } else {
      return `${d.getMonth() + 1}/${d.getDate()}`;
    }
  }, [ervRange]);

  // 10. Summary Statistics & Dynamic CO2 Axis Scale
  const latestCo2 = sesRealtimeData?.['CO2']?.[0]?.value != null && !isNaN(Number(sesRealtimeData['CO2'][0].value))
    ? Math.round(Number(sesRealtimeData['CO2'][0].value))
    : null;
  const maxErvLevel = finalErvCo2Data.reduce((max, curr) => Math.max(max, curr.erv || 0), 0);
  const avgCo2 = useMemo(() => {
    const validCo2s = finalErvCo2Data
      .map((d) => d.co2)
      .filter((v): v is number => typeof v === 'number' && !isNaN(v) && v > 0);
    return validCo2s.length > 0
      ? Math.round(validCo2s.reduce((acc, curr) => acc + curr, 0) / validCo2s.length)
      : latestCo2;
  }, [finalErvCo2Data, latestCo2]);

  const { minCo2, maxCo2 } = useMemo(() => {
    const validCo2s = finalErvCo2Data
      .map((d) => d.co2)
      .filter((v): v is number => typeof v === 'number' && !isNaN(v) && v > 0);
    if (validCo2s.length === 0) {
      return { minCo2: 300, maxCo2: 1200 };
    }
    const minVal = Math.min(...validCo2s);
    const maxVal = Math.max(...validCo2s);
    const padding = Math.max(50, Math.round((maxVal - minVal) * 0.2));
    return {
      minCo2: Math.max(0, Math.floor((minVal - padding) / 50) * 50),
      maxCo2: Math.ceil((maxVal + padding) / 50) * 50
    };
  }, [finalErvCo2Data]);

  return (
    <div className="w-full flex flex-col h-full bg-[#0a0f1a]/30 border-l border-white/20 overflow-y-auto font-sans p-4 space-y-4">
      {/* ─── Top Header ─── */}
      <SensorHeader
        sensor={sensor}
        onBack={onBack}
        onClose={onClose}
        onUpdateSensor={onUpdateSensor}
        rightSlot={
          <button
            onClick={() => setShowLinkPopup(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/25 transition-all text-xs font-medium"
            title="연동 SES 스위치 설정"
          >
            <Link2Icon className="w-3.5 h-3.5" />
            <span className="truncate max-w-[100px]">{linkedSes ? linkedSes.name : 'SES 연동'}</span>
          </button>
        }
      />

      {/* ─── Wind Speed Status Card & Quick RPC Control ─── */}
      {/* ─── Top Control Card: ERV Airflow Level & Quick Control ─── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-2xl bg-gradient-to-br from-[#131d33] to-[#0c1425] border border-white/10 shadow-xl space-y-3.5"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`p-2.5 rounded-xl border flex items-center justify-center ${
              currentErvNum === 3 ? 'bg-blue-600/20 border-blue-500/40 text-blue-400 shadow-lg shadow-blue-900/30' :
              currentErvNum === 2 ? 'bg-cyan-500/20 border-cyan-400/40 text-cyan-400 shadow-lg shadow-cyan-950/30' :
              currentErvNum === 1 ? 'bg-sky-400/15 border-sky-400/30 text-sky-300' :
              'bg-slate-800 border-slate-700 text-slate-400'
            }`}>
              <WindIcon className={`w-5 h-5 ${currentErvNum > 0 ? 'animate-pulse' : ''}`} />
            </div>
            <div>
              <div className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                현재 전열교환기(ERV) 풍량 상태
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xl font-bold font-mono text-white">
                  {currentErvNum}단계
                </span>
                <span className={`px-2 py-0.5 rounded-lg text-xs font-bold border ${
                  currentErvNum === 3 ? 'bg-blue-600 text-white border-blue-400 shadow-sm shadow-blue-500/50' :
                  currentErvNum === 2 ? 'bg-cyan-500 text-slate-950 border-cyan-300 font-extrabold' :
                  currentErvNum === 1 ? 'bg-sky-500/20 text-sky-300 border-sky-500/30' :
                  'bg-slate-800 text-slate-400 border-slate-700'
                }`}>
                  {currentStatus?.text || '정지'}
                </span>
              </div>
            </div>
          </div>

          {/* SES Link Status Pill */}
          <div className="text-right">
            <span className="text-[10px] text-slate-400 block">제어 연동</span>
            <span className="text-xs font-semibold text-cyan-300 flex items-center justify-end gap-1 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
              {linkedSes ? `${linkedSes.name} 자동 제어` : '단독 운전'}
            </span>
          </div>
        </div>

        {/* ─── 4-Step ERV Selector Buttons ─── */}
        <div className="grid grid-cols-4 gap-2 pt-1">
          {[
            { level: 0, label: '0 정지', bg: 'hover:bg-slate-700/60', activeBg: 'bg-slate-700 border-slate-500 text-white' },
            { level: 1, label: '1 약풍', bg: 'hover:bg-sky-500/20', activeBg: 'bg-sky-500/25 border-sky-400 text-sky-300 shadow-md shadow-sky-950/40' },
            { level: 2, label: '2 중풍', bg: 'hover:bg-cyan-500/25', activeBg: 'bg-cyan-500/30 border-cyan-400 text-cyan-200 shadow-lg shadow-cyan-950/50' },
            { level: 3, label: '3 강풍', bg: 'hover:bg-blue-600/30', activeBg: 'bg-blue-600 border-blue-400 text-white shadow-xl shadow-blue-900/60' },
          ].map((item) => {
            const isCurrent = currentErvNum === item.level;
            return (
              <button
                key={item.level}
                onClick={() => handleSetErv(item.level)}
                className={`py-2 px-1 rounded-xl text-xs font-bold font-roboto border transition-all duration-200 ${
                  isCurrent
                    ? `${item.activeBg} font-extrabold ring-1 ring-white/20`
                    : `bg-white/[0.04] border-white/10 text-slate-400 ${item.bg}`
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* ─── Chart 1: ERV Step-After Airflow & SES CO2 Overlay Chart (Primary Requested Feature) ─── */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-2xl bg-[#0f172a]/70 border border-white/10 shadow-xl space-y-3"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-400">
              <WindIcon className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">전열교환기 풍량 상태 & 연동 CO₂ 변화</h3>
              <p className="text-[10px] text-slate-400">
                {linkedSes ? `${linkedSes.name} CO₂ 농도 연동 자동 풍량 제어` : '실시간 풍량 계단형 상태 변화'}
              </p>
            </div>
          </div>
          <TimeRangeTabs value={ervRange} onChange={setErvRange} />
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 rounded-xl bg-white/5 border border-white/5 text-[11px]">
          <div className="flex items-center gap-3 font-mono">
            <div className="flex items-center gap-1.5 text-blue-400">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#1d4ed8]" />
              <span className="font-bold">3 강풍</span>
            </div>
            <div className="flex items-center gap-1.5 text-cyan-400">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#0284c7]" />
              <span className="font-bold">2 중풍</span>
            </div>
            <div className="flex items-center gap-1.5 text-sky-300">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#38bdf8]" />
              <span className="font-bold">1 약풍</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#64748b]" />
              <span>0 정지</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-amber-400 font-bold font-mono">
            <span className="w-2.5 h-1 rounded-full bg-amber-400" />
            <span>SES CO₂ (ppm)</span>
          </div>
        </div>

        {/* Chart Canvas */}
        <div className="h-64 w-full pt-2">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={finalErvCo2Data} margin={{ top: 10, right: 0, left: -5, bottom: 0 }}>
              <defs>
                <linearGradient id="ervAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1d4ed8" stopOpacity={0.85} />
                  <stop offset="35%" stopColor="#0284c7" stopOpacity={0.7} />
                  <stop offset="70%" stopColor="#38bdf8" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#64748b" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={[chartTimeBounds.startTs, chartTimeBounds.endTs]}
                ticks={xTicks}
                tickFormatter={formatXAxisTick}
                stroke="#64748b"
                tick={{ fill: '#94a3b8', fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              {/* Left Y-Axis: ERV Wind Level (0 ~ 3) - width=62 prevents line-wrapping */}
              <YAxis
                yAxisId="erv"
                domain={[0, 3.2]}
                ticks={[0, 1, 2, 3]}
                tickFormatter={(val) => (val === 3 ? '3 강풍' : val === 2 ? '2 중풍' : val === 1 ? '1 약풍' : '0 정지')}
                stroke="#64748b"
                tick={{ fill: '#38bdf8', fontSize: 10, fontWeight: 'bold' }}
                tickLine={false}
                axisLine={false}
                width={62}
                interval={0}
              />
              {/* Right Y-Axis: CO2 ppm - aligned right */}
              <YAxis
                yAxisId="co2"
                orientation="right"
                domain={[minCo2, maxCo2]}
                stroke="#64748b"
                tick={{ fill: '#f59e0b', fontSize: 10, fontWeight: 'bold' }}
                tickLine={false}
                axisLine={false}
                width={36}
                tickFormatter={(val) => `${val}`}
              />
              <Tooltip content={<ErvCo2CustomTooltip />} />

              {/* Step-After Area (Holds state horizontally until next telemetry timestamp) */}
              <Area
                yAxisId="erv"
                type="stepAfter"
                dataKey="erv"
                name="ERV 풍량 단계"
                stroke="#38bdf8"
                strokeWidth={3}
                fill="url(#ervAreaGrad)"
                dot={false}
                activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2, fill: '#38bdf8' }}
              />

              {/* CO2 Line Overlay (dot={false}) */}
              <Line
                yAxisId="co2"
                type="monotone"
                dataKey="co2"
                name="CO2 농도"
                stroke="#f59e0b"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: '#fbbf24', stroke: '#ffffff', strokeWidth: 2 }}
                connectNulls={true}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Bottom Statistics Cards */}
        <div className="grid grid-cols-4 gap-2 pt-2 border-t border-white/5 text-center">
          <div className="p-2 rounded-xl bg-white/5">
            <span className="text-[10px] text-slate-400 block">현재 풍량</span>
            <span className={`text-xs font-bold font-mono ${currentStatus?.color || 'text-cyan-300'}`}>
              {currentErvNum}단계 ({currentStatus?.text || '정지'})
            </span>
          </div>
          <div className="p-2 rounded-xl bg-white/5">
            <span className="text-[10px] text-slate-400 block">현재 CO₂</span>
            <span className="text-xs font-bold font-mono text-amber-400">
              {latestCo2 !== null ? `${latestCo2} ppm` : '-'}
            </span>
          </div>
          <div className="p-2 rounded-xl bg-white/5">
            <span className="text-[10px] text-slate-400 block">기간 최고 풍량</span>
            <span className="text-xs font-bold font-mono text-blue-400">
              {maxErvLevel}단계 ({maxErvLevel === 3 ? '강풍' : maxErvLevel === 2 ? '중풍' : maxErvLevel === 1 ? '약풍' : '정지'})
            </span>
          </div>
          <div className="p-2 rounded-xl bg-white/5">
            <span className="text-[10px] text-slate-400 block">기간 평균 CO₂</span>
            <span className="text-xs font-bold font-mono text-amber-300">
              {avgCo2 !== null ? `${avgCo2} ppm` : '-'}
            </span>
          </div>
        </div>
      </motion.div>

      {/* ─── SES Connect Popup Modal ─── */}
      <AnimatePresence>
        {showLinkPopup && (
          <SevSesConnectPopup
            sensor={sensor}
            currentSesId={linkedSes?.id || ''}
            onClose={() => setShowLinkPopup(false)}
            onLinked={(ses) => setLinkedSes(ses)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
